import { useQuery } from 'react-query';
import { QueryConfig } from 'react-query/types/core/types';
import reactQueryConfig from 'config/react-query-config';

export type TAsyncStatesHandlers<TResult, TError> = {
    onSuccess?: (arg0?: TResult) => void;
    onError?: (arg0?: TError) => void;
    onStart?: () => void;
};

export type TQueryHookWrapper<TResult, TError, TValues> = {
    queryKey: string;
    queryFn: (arg0: TValues) => Promise<TResult>;
    queryConfig?: QueryConfig<TResult, TError>;
    asyncStateHandlers?: TAsyncStatesHandlers<TResult, TError>;
    values: TValues;
};

/** Wrap query hook of React Query library with reaction on default async state changes: start, success, error */
const useQueryHookWrapper = <TResponse, TError, TValues = undefined>({
    queryKey,
    queryFn,
    queryConfig,
    values,
    asyncStateHandlers: { onSuccess, onError, onStart } = {},
}: TQueryHookWrapper<TResponse, TError, TValues>) => {
    return useQuery<TResponse, TError>(
        queryKey,
        () => {
            onStart && onStart();
            return queryFn(values);
        },
        {
            ...reactQueryConfig,
            ...queryConfig,
            onSuccess: (data) => {
                queryConfig?.onSuccess && queryConfig.onSuccess(data);
                onSuccess && onSuccess(data);
            },
            onError: (error) => {
                queryConfig?.onError && queryConfig.onError(error);
                onError && onError(error);
            },
        },
    );
};

export default useQueryHookWrapper;
